import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { EvidenceType, ReleaseDetail, ReleaseExecutionStep } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { ON_FAILURE_LABELS } from "./releaseVocabulary";

const EVIDENCE_TYPES: EvidenceType[] = [
  "SCREENSHOT", "FILE", "URL", "PIPELINE_LINK", "BUILD_LINK", "MONITORING_LINK",
  "TEST_REPORT", "LOG_REFERENCE", "CHANGE_TICKET", "DATABASE_SNAPSHOT", "TEXT_RESULT",
];

/**
 * Recording what happened to a step (§34, §36, §38).
 *
 * <p>Failing and skipping both demand a reason and the button stays disabled without one — the
 * server refuses them too. These are the rows an incident review reads first, and a blank there
 * is exactly where its question is.
 *
 * <p>When a step that would trigger a rollback fails, this says so plainly and does nothing.
 * §38 is right that a system which starts restoring snapshots by itself will eventually do so at
 * the worst possible moment.
 */
export function StepOutcomeDrawer({
  release,
  step,
  kind,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  step: ReleaseExecutionStep | null;
  kind: "complete" | "fail" | "skip" | "retry";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = useState("");
  const [evidenceType, setEvidenceType] = useState<EvidenceType>("TEXT_RESULT");
  const [evidenceRef, setEvidenceRef] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (step) {
      setText("");
      setEvidenceType("TEXT_RESULT");
      setEvidenceRef("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, kind]);

  if (!step) {
    return null;
  }

  const needsReason = kind !== "complete";
  const needsEvidence = kind === "complete" && step.evidenceRequired
    && step.evidence.length === 0;
  const canSubmit =
    (!needsReason || text.trim() !== "") && (!needsEvidence || evidenceRef.trim() !== "");

  const titles = {
    complete: "Mark this step done",
    fail: "Record a failure",
    skip: "Skip this step",
    retry: "Try this step again",
  };

  const submit = () =>
    void run(
      async () => {
        if (needsEvidence) {
          await api.post(
            `/api/v1/releases/${release.id}/execution-steps/${step.id}/evidence`,
            {
              evidenceType,
              reference: evidenceRef.trim(),
              description: null,
              attachmentId: null,
            },
          );
        }
        const base = `/api/v1/releases/${release.id}/execution-steps/${step.id}`;
        if (kind === "complete") {
          await api.post(`${base}/complete`, {
            result: text.trim() === "" ? null : text.trim(),
            // §35 is checked on completing as well as on starting, so a step ticked off without
            // ever being started cannot go round the thing it waits for.
            override: step.waitingFor.length > 0,
            lockVersion: step.lockVersion,
          });
        } else if (kind === "fail") {
          await api.post(`${base}/fail`, {
            reason: text.trim(),
            result: null,
            lockVersion: step.lockVersion,
          });
        } else if (kind === "skip") {
          await api.post(`${base}/skip`, {
            reason: text.trim(),
            lockVersion: step.lockVersion,
          });
        } else {
          await api.post(`${base}/retry`, {
            reason: text.trim(),
            lockVersion: step.lockVersion,
          });
        }
      },
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open
      onClose={onClose}
      title={titles[kind]}
      subtitle={`${step.sequenceNo}. ${step.title}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={kind === "fail" ? "danger" : "primary"}
            onClick={submit}
            disabled={!canSubmit}
            loading={saving}
          >
            {saving ? "Recording…" : titles[kind]}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {kind === "retry" && (
          <InlineAlert tone="info">
            The failure stays on the record. This adds an attempt beside it, so the runbook still
            says how many it took.
          </InlineAlert>
        )}

        {kind === "fail" && step.onFailureAction !== "CONTINUE" && (
          <InlineAlert tone="warning">
            The plan for this step says:{" "}
            <strong>{ON_FAILURE_LABELS[step.onFailureAction].toLowerCase()}</strong>. Recording
            the failure does not do any of that — it is written down so somebody can decide.
          </InlineAlert>
        )}

        <Field
          label={
            kind === "complete"
              ? "Result"
              : kind === "fail"
                ? "What went wrong"
                : kind === "retry"
                  ? "Why it is worth another attempt"
                  : "Why skip it"
          }
          required={needsReason}
          hint={
            kind === "complete"
              ? "Optional — a snapshot id, an exit code, whatever is worth keeping."
              : kind === "fail"
                ? "The first thing anybody reads afterwards."
                : kind === "retry"
                  ? "What changed since it failed, so the next reader knows why you went again."
                  : "Skipping is a decision, not an omission."
          }
        >
          <Textarea
            rows={3}
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </Field>

        {needsEvidence && (
          <>
            <InlineAlert tone="info">
              This step was set up to require evidence, so it cannot be completed without
              something attached.
            </InlineAlert>
            <div className="grid grid-cols-[1fr_2fr] gap-3">
              <Field label="Kind">
                <Select
                  value={evidenceType}
                  onChange={(event) => setEvidenceType(event.target.value as EvidenceType)}
                >
                  {EVIDENCE_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {value.toLowerCase().replace(/_/g, " ")}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Reference" required>
                <Input
                  value={evidenceRef}
                  placeholder="s3://backups/prod-2026-09-03"
                  onChange={(event) => setEvidenceRef(event.target.value)}
                />
              </Field>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
